import { safeReturnTo } from "@shared/return-to";

export { safeReturnTo };

const RETURN_TO_KEY = "crosbe-auth-return-to";

export interface ReturnToStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getSessionStorage(): ReturnToStorage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * The returnTo carries the page's path, search and hash whole, by design: a
 * deep link (a Weigh-Out sheet, a filtered list) lives in its query string.
 * That is safe only while the app never puts a secret in a URL (no tokens,
 * no one-time codes, no personal data in a query or hash), because this value
 * rides through /login, the OAuth state and sessionStorage. Keep it that way.
 */
export function returnToPath(route: string, returnTo: string): string {
  return `${route}?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`;
}

export function readReturnTo(
  search: string,
  storage: ReturnToStorage | undefined = getSessionStorage(),
): string {
  const queryValue = new URLSearchParams(search).get("returnTo");
  if (queryValue !== null) return safeReturnTo(queryValue);

  try {
    return safeReturnTo(storage?.getItem(RETURN_TO_KEY));
  } catch {
    return "/";
  }
}

export function rememberReturnTo(
  returnTo: string,
  storage: ReturnToStorage | undefined = getSessionStorage(),
): void {
  try {
    storage?.setItem(RETURN_TO_KEY, safeReturnTo(returnTo));
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // returnTo query carried by better-auth remains the primary mechanism.
  }
}

export function consumeReturnTo(
  search: string,
  storage: ReturnToStorage | undefined = getSessionStorage(),
): string {
  const returnTo = readReturnTo(search, storage);
  try {
    storage?.removeItem(RETURN_TO_KEY);
  } catch {
    // A failed cleanup is harmless; auth-flow paths are never valid targets.
  }
  return returnTo;
}
